import { ArrowRightOutlined } from '@ant-design/icons';
import { EnumCambiamentoStatoRequestTransizione } from '@framework/api-client/api/inquiries-compile/schemas/enumCambiamentoStatoRequestTransizione.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { SubmissionModuleState } from '@framework/common-web/frontoffice/module/fsm/SubmissionModuleState.ts';
import {
  type IModuleStateMachineRecord,
  ModuleStateMachine,
} from '@framework/frontoffice-submission/module/fsm/ModuleStateMachine.ts';
import { ActionButtonContainer } from '@framework/frontoffice-submission/submission/signer/ActionButtonContainer.tsx';
import { ActionMessage } from '@framework/frontoffice-submission/submission/signer/ActionMessage.tsx';
import { SubmissionModuleLogic } from '@framework/frontoffice-submission/submission/submission-module/SubmissionModuleLogic.ts';
import { AsyncButton } from '@framework/ui/components/button/AsyncButton.tsx';
import { Space, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSubmissionModuleStateChanger } from './useSubmissionModuleStateChanger.ts';

export interface IReadyToSignButtonProps {
  data: IModuleStateMachineRecord;
}

export function ReadyToSignButton(props: IReadyToSignButtonProps) {
  const { data } = props;

  const currentUser = useCurrentUser();
  const { t } = useTranslation();
  const { isDirty } = useFormState();

  const isModuleSavedOnce = Boolean(data.module?.statoValidazione);
  const isModuleValid = SubmissionModuleLogic.isValid(
    data.module?.statoValidazione,
  );

  const canSign = useMemo(() => {
    return ModuleStateMachine.canTransition(data, {
      from: SubmissionModuleState.Compilation,
      to: SubmissionModuleState.Sign,
      user: currentUser,
    });
  }, [currentUser, data]);

  const { handleClick } = useSubmissionModuleStateChanger({
    data,
    successMessage: t(
      'submission-action-button.modulo-pronto-per-la-firma',
      'Modulo pronto per la firma',
    ),
  });

  const disabled = isDirty || !isModuleValid;

  const tooltip = useMemo(() => {
    if (isDirty) {
      return t(
        'submission-action-button.salva-modulo',
        'Salva il modulo per continuare',
      );
    }
    if (!isModuleSavedOnce) {
      return t(
        'submission-action-button.salva-modulo',
        'Salva il modulo per continuare',
      );
    }
    if (!isModuleValid) {
      return t(
        'submission-action-button.modulo-non-valido',
        'Il modulo non è valido',
      );
    }

    return undefined;
  }, [isDirty, isModuleSavedOnce, isModuleValid, t]);

  if (!canSign.allowed && canSign.message === 'no-action-permission') {
    return (
      <ActionMessage
        message={t(
          'submission-action-button.in-attesa-compilazione-delegato',
          'In attesa della compilazione da parte del delegato.',
        )}
        type={'info'}
      />
    );
  }

  if (!canSign.allowed) return null;

  return (
    <ActionButtonContainer
      message={
        <ActionMessage
          message={
            tooltip ??
            t(
              'submission-action-button.per-procedere-alla-firma-segnare-come-pronto',
              'Per procedere alla firma segnare il modulo come Pronto per la firma.',
            )
          }
          type={tooltip ? 'warning' : 'info'}
        />
      }
      button={
        <Tooltip title={tooltip}>
          <AsyncButton
            block
            type="primary"
            size="large"
            onClick={async () => {
              await handleClick(
                EnumCambiamentoStatoRequestTransizione.PRONTO_ALLA_FIRMA,
              );
            }}
            disabled={disabled}
          >
            <Space>
              {t(
                'submission-action-button.pronto-per-la-firma',
                'Pronto per la firma',
              )}
              <ArrowRightOutlined aria-hidden="true" />
            </Space>
          </AsyncButton>
        </Tooltip>
      }
    />
  );
}
